import { useCallback, useEffect, useState } from 'react';

import { isSSR } from '@telialt/utils/src/helpers';
import { sessionPing } from '@telialt/utils/src/SSO/api';
import { useUserDataQueryBase } from '@telialt/utils/src/SSO/useUserData';
import { getIsUserAuthorized, getSessionExpireTime } from '@telialt/utils/src/SSO/utils';

export const useSessionExpirations = (beforeEndTime: number, checkInterval: number) => {
    const [sessionStatus, setSessionStatus] = useState<'near-expire' | 'expired'>();
    const { userData } = useUserDataQueryBase({ isEnabled: !isSSR() });
    const isRememberMe: boolean | undefined = userData?.rememberMe;

    const check = useCallback(() => {
        const sessionExpireTime = getSessionExpireTime();
        // If cookie does not exist, executing ping which should update cookie
        if (!sessionExpireTime) {
            sessionPing();

            return;
        }

        const sessionTimeLeft = sessionExpireTime - Date.now();
        if (sessionTimeLeft <= 0) {
            setSessionStatus('expired');
        } else if (sessionTimeLeft - beforeEndTime <= 0) {
            setSessionStatus('near-expire');
        } else {
            setSessionStatus(undefined);
        }
    }, [beforeEndTime]);

    useEffect(() => {
        const isCheckEnabled = getIsUserAuthorized() && isRememberMe === false;

        const timer = isCheckEnabled ? window.setInterval(check, checkInterval) : undefined;

        return () => window.clearInterval(timer);
    }, [check, checkInterval, isRememberMe]);

    return { isNearExpire: sessionStatus === 'near-expire', isExpired: sessionStatus === 'expired', check };
};
